import { useAuth0 } from '@auth0/auth0-react';
import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { PageLoader } from 'src/components/PageLoader';

import { isActiveSessionUser, isFreshLogin } from './sessionVerification';

// Blocks rendering until the cached session matches the active
// Auth0 user; otherwise clears the local cache and logs in again.
export const SessionGate = ({ children }: PropsWithChildren) => {
  const { isAuthenticated, user, logout, loginWithRedirect } = useAuth0();
  const [verifiedSub, setVerifiedSub] = useState<string | undefined>(undefined);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const checkedSub = useRef<string | undefined>(undefined);
  const sub = isAuthenticated ? user?.sub : undefined;

  const needsCheck = Boolean(sub) && !isFreshLogin() && verifiedSub !== sub;

  useEffect(() => {
    if (!needsCheck || !sub || checkedSub.current === sub) return;
    checkedSub.current = sub;

    const verify = async () => {
      if (await isActiveSessionUser(sub)) {
        setVerifiedSub(sub);
        return;
      }
      setIsRedirecting(true);
      try {
        await logout({ openUrl: false });
        await loginWithRedirect({
          appState: { returnTo: `${globalThis.location.pathname}${globalThis.location.search}` },
        });
      } catch {
        // Full Auth0 logout: still fails closed, never leaves the loader stuck.
        logout({ logoutParams: { returnTo: globalThis.location.origin } });
      }
    };
    verify();
  }, [needsCheck, sub, logout, loginWithRedirect]);

  if (needsCheck || isRedirecting) {
    return <PageLoader />;
  }

  return <>{children}</>;
};
